/**
 * Bulk Correction Panel
 * 
 * Allows users to correct multiple transactions at once with common patterns
 */

import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { AccountSelector } from '@/components/validation/account-selector';
import { useToast } from '@/hooks/use-toast';
import { useUpdateTransaction } from '@/hooks/use-transactions';
import { 
  CheckCircle, 
  XCircle, 
  Edit3, 
  Zap,
  TrendingUp,
  Users,
  Building
} from 'lucide-react';

interface BulkCorrectionPanelProps {
  selectedTransactions: any[];
  onClose: () => void;
  onApplyCorrection: (transactionIds: string[], updates: any) => void;
  className?: string;
}

interface CorrectionTemplate {
  id: string;
  name: string;
  description: string;
  debitAccount: string;
  creditAccount: string;
  vatRate: number;
  icon: React.ReactNode;
  color: string;
  usageCount: number;
}

const correctionTemplates: CorrectionTemplate[] = [
  {
    id: 'office-supplies',
    name: 'Büromaterial',
    description: 'Papier, Stifte, Druckerpatronen',
    debitAccount: '6503',
    creditAccount: '1020',
    vatRate: 8.1,
    icon: <Edit3 className="h-4 w-4" />,
    color: 'bg-blue-100 text-blue-800',
    usageCount: 12
  },
  {
    id: 'software-subscriptions',
    name: 'Software-Abonnements',
    description: 'SaaS, Cloud-Services, Lizenzen',
    debitAccount: '6700',
    creditAccount: '1020',
    vatRate: 8.1,
    icon: <Zap className="h-4 w-4" />,
    color: 'bg-purple-100 text-purple-800',
    usageCount: 8
  },
  {
    id: 'marketing',
    name: 'Marketing & Werbung',
    description: 'Google Ads, Facebook, Print-Werbung',
    debitAccount: '6600',
    creditAccount: '1020',
    vatRate: 8.1,
    icon: <TrendingUp className="h-4 w-4" />,
    color: 'bg-green-100 text-green-800',
    usageCount: 15
  },
  {
    id: 'travel-expenses',
    name: 'Reisespesen',
    description: 'Zug, Hotel, Verpflegung',
    debitAccount: '5832',
    creditAccount: '1020',
    vatRate: 8.1,
    icon: <Users className="h-4 w-4" />,
    color: 'bg-orange-100 text-orange-800',
    usageCount: 6
  },
  {
    id: 'rent-utilities',
    name: 'Miete & Nebenkosten',
    description: 'Büromiete, Strom, Internet',
    debitAccount: '6000',
    creditAccount: '1020',
    vatRate: 8.1,
    icon: <Building className="h-4 w-4" />,
    color: 'bg-gray-100 text-gray-800',
    usageCount: 24
  }
];

export const BulkCorrectionPanel = ({ 
  selectedTransactions, 
  onClose, 
  onApplyCorrection,
  className = '' 
}: BulkCorrectionPanelProps) => {
  const [selectedTemplate, setSelectedTemplate] = useState<CorrectionTemplate | null>(null);
  const [customDebitAccount, setCustomDebitAccount] = useState('');
  const [customCreditAccount, setCustomCreditAccount] = useState('1020');
  const [customVatRate, setCustomVatRate] = useState(8.1);
  const [isApplying, setIsApplying] = useState(false);
  const { toast } = useToast();
  const updateTransaction = useUpdateTransaction();

  const selectedCount = selectedTransactions.length;
  const totalAmount = selectedTransactions.reduce((sum, t) => sum + (t.amountRaw || 0), 0);

  const handleApplyTemplate = async (template: CorrectionTemplate) => {
    setIsApplying(true);
    
    try {
      const updates = {
        account_number: template.debitAccount,
        credit_account: template.creditAccount,
        vat_rate: template.vatRate,
        needs_review: false,
        human_verified: true
      };

      // Apply to all selected transactions
      const transactionIds = selectedTransactions.map(t => t.id);
      await onApplyCorrection(transactionIds, updates);

      toast({
        title: 'Bulk-Korrektur angewendet',
        description: `${selectedCount} Transaktionen wurden mit "${template.name}" korrigiert`,
      });

      onClose();
    } catch (error) {
      toast({
        title: 'Fehler',
        description: 'Bulk-Korrektur konnte nicht angewendet werden',
        variant: 'destructive',
      });
    } finally {
      setIsApplying(false);
    }
  };

  const handleApplyCustom = async () => {
    if (!customDebitAccount) {
      toast({
        title: 'Fehler',
        description: 'Bitte wählen Sie ein Debit-Konto aus',
        variant: 'destructive',
      });
      return;
    }

    setIsApplying(true);
    
    try {
      const updates = {
        account_number: customDebitAccount,
        credit_account: customCreditAccount,
        vat_rate: customVatRate,
        needs_review: false,
        human_verified: true
      };

      const transactionIds = selectedTransactions.map(t => t.id);
      await onApplyCorrection(transactionIds, updates);

      toast({
        title: 'Bulk-Korrektur angewendet',
        description: `${selectedCount} Transaktionen wurden mit benutzerdefinierten Einstellungen korrigiert`,
      });

      onClose();
    } catch (error) {
      toast({
        title: 'Fehler',
        description: 'Bulk-Korrektur konnte nicht angewendet werden',
        variant: 'destructive',
      });
    } finally {
      setIsApplying(false);
    }
  };

  return (
    <div className={`bg-white border border-gray-200 rounded-lg shadow-lg ${className}`}>
      {/* Header */}
      <div className="p-4 border-b border-gray-200">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-lg font-semibold text-gray-900">
              Bulk-Korrektur
            </h3>
            <p className="text-sm text-gray-600">
              {selectedCount} Transaktionen ausgewählt • {totalAmount.toLocaleString('de-CH', { style: 'currency', currency: 'CHF' })}
            </p>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <XCircle className="h-5 w-5" />
          </Button>
        </div>
      </div>

      {/* Content */}
      <div className="p-4 space-y-6">
        {/* Quick Templates */}
        <div>
          <h4 className="text-sm font-medium text-gray-900 mb-3">
            Schnellvorlagen (basierend auf häufigen Mustern)
          </h4>
          <div className="grid grid-cols-1 gap-2">
            {correctionTemplates.map((template) => (
              <button
                key={template.id}
                onClick={() => handleApplyTemplate(template)}
                disabled={isApplying}
                className={`p-3 rounded-lg border-2 transition-all text-left hover:shadow-md disabled:opacity-50 ${
                  selectedTemplate?.id === template.id
                    ? 'border-blue-500 bg-blue-50'
                    : 'border-gray-200 hover:border-gray-300'
                }`}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className={`p-2 rounded-lg ${template.color}`}>
                      {template.icon}
                    </div>
                    <div>
                      <div className="font-medium text-gray-900">{template.name}</div>
                      <div className="text-sm text-gray-600">{template.description}</div>
                      <div className="text-xs text-gray-500">
                        {template.debitAccount} → {template.creditAccount} • {template.vatRate}% MwSt
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge variant="secondary" className="text-xs">
                      {template.usageCount}x verwendet
                    </Badge>
                    <CheckCircle className="h-4 w-4 text-gray-400" />
                  </div>
                </div>
              </button>
            ))}
          </div>
        </div>

        {/* Custom Correction */}
        <div className="border-t border-gray-200 pt-4">
          <h4 className="text-sm font-medium text-gray-900 mb-3">
            Benutzerdefinierte Korrektur
          </h4>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">
                Debit-Konto
              </label>
              <AccountSelector
                value={customDebitAccount}
                onChange={setCustomDebitAccount}
                placeholder="Konto auswählen"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">
                Credit-Konto
              </label>
              <AccountSelector
                value={customCreditAccount}
                onChange={setCustomCreditAccount}
                placeholder="Konto auswählen"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">
                MwSt-Satz
              </label>
              <select
                value={customVatRate}
                onChange={(e) => setCustomVatRate(Number(e.target.value))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value={0}>0% (Befreit)</option>
                <option value={2.6}>2.6% (Reduziert)</option>
                <option value={8.1}>8.1% (Standard)</option>
              </select>
            </div>
          </div>
          <div className="mt-4">
            <Button
              onClick={handleApplyCustom}
              disabled={isApplying || !customDebitAccount}
              className="w-full"
            >
              {isApplying ? 'Wird angewendet...' : 'Benutzerdefinierte Korrektur anwenden'}
            </Button>
          </div>
        </div>

        {/* Summary */}
        <div className="bg-gray-50 rounded-lg p-3">
          <div className="text-sm text-gray-600">
            <strong>Zusammenfassung:</strong> Diese Korrektur wird auf alle {selectedCount} ausgewählten Transaktionen angewendet. 
            Die Transaktionen werden als "menschlich verifiziert" markiert und aus der Überprüfung entfernt.
          </div>
        </div>
      </div>
    </div>
  );
};
